import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, User, Crown, ChevronDown, ChevronRight, Plus, X, Search, Filter, MoreVertical, Edit2, Trash2, Eye, EyeOff, Copy, RefreshCw, Check } from 'lucide-react';
import { fetchSecrets, createSecret, updateSecret, deleteSecret } from '../services/secretsApi';
import MemberAvatar from './MemberAvatar';
import UserAvatar from './UserAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';

const SecretsVault = () => {
    const navigate = useNavigate();
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [activeTab, setActiveTab] = useState('vault'); // vault or generator
    const [showAddModal, setShowAddModal] = useState(false);
    const [showPasswordGenerator, setShowPasswordGenerator] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name'); // name, date
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [editingSecret, setEditingSecret] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    
    // Form states
    const [title, setTitle] = useState('');
    const [website, setWebsite] = useState('');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [notes, setNotes] = useState('');
    const [sharedWith, setSharedWith] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    
    // Password generator states
    const [genLength, setGenLength] = useState(12);
    const [useUppercase, setUseUppercase] = useState(true);
    const [useNumbers, setUseNumbers] = useState(true);
    const [useSymbols, setUseSymbols] = useState(true);
    const [uppercaseCount, setUppercaseCount] = useState(2);
    const [numbersCount, setNumbersCount] = useState(2);
    const [symbolsCount, setSymbolsCount] = useState(2);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [copied, setCopied] = useState(false);
    
    // Secrets data
    const [secrets, setSecrets] = useState([]);
    
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);
    const filterMenuRef = useRef(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('Failed to load families');
                }
                
                const familiesData = await response.json();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }
                
                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }
                
                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }
                
                setSelectedFamily(familyToSelect);
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load secrets when family changes
    useEffect(() => {
        const loadSecretsData = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchSecrets(selectedFamily.id);
                setSecrets(response.items || []);
            } catch (error) {
                console.error('Error loading secrets:', error);
            }
        };
        loadSecretsData();
    }, [selectedFamily]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
                setShowFilterMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (showAddModal || showPasswordGenerator || showPremiumModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [showAddModal, showPasswordGenerator, showPremiumModal]);

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id.toString());
        setShowFamilyDropdown(false);
    };

    const handleAddSecret = () => {
        if (!title) return;

        const newSecret = {
            id: Date.now(),
            title,
            website,
            username,
            email,
            password,
            notes,
            sharedWith,
            createdBy: selectedFamily?.members?.[0] || 'You',
            createdAt: new Date().toISOString(),
            image: website ? `https://www.google.com/s2/favicons?domain=${website}&sz=64` : null
        };

        if (editingSecret) {
            setSecrets(secrets.map(s => s.id === editingSecret.id ? { ...editingSecret, ...newSecret } : s));
        } else {
            setSecrets([...secrets, newSecret]);
        }

        resetForm();
    };

    const handleEditSecret = (secret) => {
        setEditingSecret(secret);
        setTitle(secret.title);
        setWebsite(secret.website || '');
        setUsername(secret.username || '');
        setEmail(secret.email || '');
        setPassword(secret.password || '');
        setNotes(secret.notes || '');
        setSharedWith(secret.sharedWith || []);
        setShowAddModal(true);
        setOpenMenuId(null);
    };

    const handleDeleteSecret = (id) => {
        if (window.confirm('Are you sure you want to delete this secret?')) {
            setSecrets(secrets.filter(s => s.id !== id));
        }
        setOpenMenuId(null);
    };

    const resetForm = () => {
        setTitle('');
        setWebsite('');
        setUsername('');
        setEmail('');
        setPassword('');
        setNotes('');
        setSharedWith([]);
        setEditingSecret(null);
        setShowAddModal(false);
        setShowPassword(false);
    };

    const generatePassword = () => {
        const lowercase = 'abcdefghijklmnopqrstuvwxyz';
        const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const numbers = '0123456789';
        // Only safe ASCII symbols - excluding space, backtick, quotes, and backslash
        const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?/~';

        let chars = lowercase;
        let password = '';
        
        // Add required characters based on settings
        if (useUppercase) {
            chars += uppercase;
            for (let i = 0; i < uppercaseCount; i++) {
                password += uppercase[Math.floor(Math.random() * uppercase.length)];
            }
        }
        
        if (useNumbers) {
            chars += numbers;
            for (let i = 0; i < numbersCount; i++) {
                password += numbers[Math.floor(Math.random() * numbers.length)];
            }
        }
        
        if (useSymbols) {
            chars += symbols;
            for (let i = 0; i < symbolsCount; i++) {
                password += symbols[Math.floor(Math.random() * symbols.length)];
            }
        }

        // Fill remaining length with random characters from all enabled types
        const remainingLength = genLength - password.length;
        for (let i = 0; i < remainingLength; i++) {
            password += chars[Math.floor(Math.random() * chars.length)];
        }

        // Shuffle the password
        password = password.split('').sort(() => Math.random() - 0.5).join('');
        
        setGeneratedPassword(password);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const useGeneratedPassword = () => {
        setPassword(generatedPassword);
        setShowPasswordGenerator(false);
        setGeneratedPassword('');
    };

    const getFamilyMembers = () => {
        if (!selectedFamily || !selectedFamily.members) return [];
        // Filter out empty or invalid member objects
        return selectedFamily.members.filter(member => member && typeof member === 'object' && member.name);
    };

    const toggleMemberAccess = (member) => {
        const memberName = typeof member === 'object' ? member.name : member;
        if (sharedWith.includes(memberName)) {
            setSharedWith(sharedWith.filter(m => m !== memberName));
        } else {
            setSharedWith([...sharedWith, memberName]);
        }
    };

    const getFilteredSecrets = () => {
        let filtered = [...secrets];

        if (searchQuery) {
            filtered = filtered.filter(s => 
                s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                s.website?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                s.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                s.email?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (sortBy === 'name') {
            filtered.sort((a, b) => a.title.localeCompare(b.title));
        } else if (sortBy === 'date') {
            filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }

        return filtered;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>
                            
                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img 
                                                src={selectedFamily.photo} 
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showFamilyDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {showFamilyDropdown && (
                                    <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        {families.length > 0 ? (
                                            <>
                                                {families.map((family) => (
                                                    <button
                                                        key={family.id}
                                                        onClick={() => {
                                                            setSelectedFamily(family);
                                                            localStorage.setItem('selectedFamilyId', family.id.toString());
                                                            setShowFamilyDropdown(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 ${
                                                            selectedFamily?.id === family.id ? 'bg-blue-50' : ''
                                                        }`}
                                                    >
                                                        <img 
                                                            src={family.photo} 
                                                            alt={family.name}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                        />
                                                        <div className="flex-1">
                                                            <p className="font-semibold text-gray-800">{family.name}</p>
                                                            {family.tagline && (
                                                                <p className="text-xs text-gray-500 italic truncate">"{family.tagline}"</p>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                                <div className="border-t border-gray-200 my-2"></div>
                                            </>
                                        ) : (
                                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                No families yet
                                            </div>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowFamilyDropdown(false);
                                                navigate('/famlis');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-primary-blue/5 flex items-center gap-3 text-primary-blue font-semibold"
                                        >
                                            <Plus className="w-5 h-5" />
                                            <span>Add Family</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {/* Premium Button */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowPremiumModal(true)}
                                    className="relative px-4 py-2 rounded-lg font-semibold text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-2 border-white/50 overflow-hidden group"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(236, 72, 153, 0.6)'
                                    }}
                                >
                                    <span className="relative z-10 flex items-center gap-2">
                                        <span className="hidden sm:inline">Go Premium</span>
                                        <span className="sm:hidden">Premium</span>
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                </button>
                                
                                {/* Golden Crown Overlay */}
                                <div className="absolute -top-1 -right-1 transform rotate-12">
                                    <Crown className="w-5 h-5 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
                                </div>
                            </div>
                            
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                >
                                    <UserAvatar size="medium" />
                                </button>

                                {/* User Dropdown Menu */}
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-200">
                                            <p className="text-sm font-semibold text-gray-800">User Account</p>
                                            <p className="text-xs text-gray-500">Manage your profile</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Profile settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <User className="w-5 h-5 text-gray-600" />
                                            <span>Profile</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Settings className="w-5 h-5 text-gray-600" />
                                            <span>Settings</span>
                                        </button>
                                        <div className="border-t border-gray-200 my-2"></div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                handleLogout();
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Secrets Vault</span>
                </div>

                {/* Tabs */}
                <div className="mb-6">
                    <div className="flex gap-4 border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('vault')}
                            className={`px-4 py-3 font-semibold transition-colors relative ${
                                activeTab === 'vault'
                                    ? 'text-primary-blue'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            Secrets Vault
                            {activeTab === 'vault' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('generator')}
                            className={`px-4 py-3 font-semibold transition-colors relative ${
                                activeTab === 'generator'
                                    ? 'text-primary-blue'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            Password Generator
                            {activeTab === 'generator' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue"></div>
                            )}
                        </button>
                    </div>
                </div>

                {activeTab === 'vault' ? (
                    <>
                        {/* Header */}
                        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <h2 className="text-3xl font-bold text-gray-900">Secrets Vault</h2>
                            <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                                {/* Search */}
                                <div className="relative flex-1 sm:flex-none">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search secrets..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent w-full sm:w-64"
                                    />
                                </div>

                                {/* Sort */}
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                >
                                    <option value="name">Sort by Name</option>
                                    <option value="date">Sort by Date</option>
                                </select>

                                {/* Add Button */}
                                <button
                                    onClick={() => setShowAddModal(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                                >
                                    <Plus className="w-5 h-5" />
                                    Add Creds
                                </button>
                            </div>
                        </div>

                        {/* Secrets List */}
                        <div className="space-y-4">
                            {getFilteredSecrets().length === 0 ? (
                                <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                    <p className="text-gray-500">No secrets yet. Click "Add Creds" to get started!</p>
                                </div>
                            ) : (
                                getFilteredSecrets().map((secret) => (
                                    <div key={secret.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                                        <div className="flex items-start gap-4">
                                            {/* Image/Icon */}
                                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                {secret.image ? (
                                                    <img src={secret.image} alt={secret.title} className="w-8 h-8" />
                                                ) : (
                                                    <div className="w-8 h-8 rounded bg-gradient-to-br from-blue-500 to-purple-600"></div>
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-lg font-semibold text-gray-900 truncate">
                                                    {secret.title}
                                                </h3>
                                                {secret.website && (
                                                    <a
                                                        href={secret.website.startsWith('http') ? secret.website : `https://${secret.website}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-blue-600 hover:underline truncate block"
                                                    >
                                                        {secret.website}
                                                    </a>
                                                )}
                                                <div className="mt-1 text-sm text-gray-600">
                                                    {secret.username && <span>{secret.username}</span>}
                                                    {secret.username && secret.email && <span className="mx-2">•</span>}
                                                    {secret.email && <span>{secret.email}</span>}
                                                </div>
                                            </div>

                                            {/* Shared With */}
                                            {secret.sharedWith && secret.sharedWith.length > 0 && (
                                                <div className="flex items-center gap-2">
                                                    <div className="flex -space-x-2">
                                                        {secret.sharedWith.slice(0, 3).map((member, index) => (
                                                            <div key={index} className="relative">
                                                                <MemberAvatar name={member} size="sm" className="border-2 border-white" />
                                                            </div>
                                                        ))}
                                                        {secret.sharedWith.length > 3 && (
                                                            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-bold border-2 border-white">
                                                                +{secret.sharedWith.length - 3}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Menu */}
                                            <div className="relative">
                                                <button
                                                    onClick={() => setOpenMenuId(openMenuId === secret.id ? null : secret.id)}
                                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                >
                                                    <MoreVertical className="w-5 h-5 text-gray-600" />
                                                </button>

                                                {openMenuId === secret.id && (
                                                    <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                        <button
                                                            onClick={() => handleEditSecret(secret)}
                                                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                            <span>Edit</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteSecret(secret.id)}
                                                            className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                            <span>Delete</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                ) : (
                    /* Password Generator Tab */
                    <div className="max-w-2xl mx-auto">
                        <div className="bg-white rounded-xl shadow-md p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Generate Password</h2>

                            {/* Generated Password Display */}
                            {generatedPassword && (
                                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between gap-4">
                                        <code className="text-lg font-mono flex-1 break-all">{generatedPassword}</code>
                                        <button
                                            onClick={() => copyToClipboard(generatedPassword)}
                                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                            title="Copy"
                                        >
                                            {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-gray-600" />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Length */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Length: {genLength} characters
                                </label>
                                <input
                                    type="range"
                                    min="6"
                                    max="32"
                                    value={genLength}
                                    onChange={(e) => setGenLength(Number(e.target.value))}
                                    className="w-full"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>6</span>
                                    <span>32</span>
                                </div>
                            </div>

                            {/* Options */}
                            <div className="space-y-4 mb-6">
                                {/* Lowercase (always on) */}
                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <label className="text-sm font-medium text-gray-700">Lowercase (a-z)</label>
                                    <input
                                        type="checkbox"
                                        checked={true}
                                        disabled
                                        className="w-5 h-5"
                                    />
                                </div>

                                {/* Uppercase */}
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Uppercase (A-Z)</label>
                                        <input
                                            type="checkbox"
                                            checked={useUppercase}
                                            onChange={(e) => setUseUppercase(e.target.checked)}
                                            className="w-5 h-5"
                                        />
                                    </div>
                                    {useUppercase && (
                                        <div className="mt-3">
                                            <label className="block text-xs text-gray-600 mb-1">Count: {uppercaseCount}</label>
                                            <input
                                                type="range"
                                                min="1"
                                                max={Math.floor(genLength / 2)}
                                                value={uppercaseCount}
                                                onChange={(e) => setUppercaseCount(Number(e.target.value))}
                                                className="w-full"
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                <span>1</span>
                                                <span>{Math.floor(genLength / 2)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Numbers */}
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Numbers (0-9)</label>
                                        <input
                                            type="checkbox"
                                            checked={useNumbers}
                                            onChange={(e) => setUseNumbers(e.target.checked)}
                                            className="w-5 h-5"
                                        />
                                    </div>
                                    {useNumbers && (
                                        <div className="mt-3">
                                            <label className="block text-xs text-gray-600 mb-1">Count: {numbersCount}</label>
                                            <input
                                                type="range"
                                                min="1"
                                                max={Math.floor(genLength / 2)}
                                                value={numbersCount}
                                                onChange={(e) => setNumbersCount(Number(e.target.value))}
                                                className="w-full"
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                <span>1</span>
                                                <span>{Math.floor(genLength / 2)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Symbols */}
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Symbols (!@#$...)</label>
                                        <input
                                            type="checkbox"
                                            checked={useSymbols}
                                            onChange={(e) => setUseSymbols(e.target.checked)}
                                            className="w-5 h-5"
                                        />
                                    </div>
                                    {useSymbols && (
                                        <div className="mt-3">
                                            <label className="block text-xs text-gray-600 mb-1">Count: {symbolsCount}</label>
                                            <input
                                                type="range"
                                                min="1"
                                                max={Math.floor(genLength / 2)}
                                                value={symbolsCount}
                                                onChange={(e) => setSymbolsCount(Number(e.target.value))}
                                                className="w-full"
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                <span>1</span>
                                                <span>{Math.floor(genLength / 2)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Generate Button */}
                            <button
                                onClick={generatePassword}
                                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Generate Password
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Edit Secret Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingSecret ? 'Edit' : 'Add'} Secret
                            </h2>
                            <button
                                onClick={resetForm}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="e.g., Gmail, Netflix, Bank"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Website */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Website / Link</label>
                                <input
                                    type="text"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    placeholder="e.g., gmail.com"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Username */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Optional"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Optional"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Optional"
                                            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowPasswordGenerator(true)}
                                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                        Generate
                                    </button>
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Optional notes..."
                                    rows="3"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent resize-none"
                                />
                            </div>

                            {/* Share With */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Share with Family Members</label>
                                {getFamilyMembers().length > 0 ? (
                                    <div className="space-y-2">
                                        {getFamilyMembers().map((member, index) => (
                                            <MemberSelector
                                                key={index}
                                                name={member.name}
                                                photo={member.photo}
                                                selected={sharedWith.includes(member.name)}
                                                    onChange={() => toggleMemberAccess(member)}
                                                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                                                />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-center">
                                        <p className="text-sm text-gray-600">
                                            No family members found. Add members to your family in the{' '}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    resetForm();
                                                    navigate('/famlis');
                                                }}
                                                className="text-primary-blue hover:underline font-medium"
                                            >
                                                Mi Famlis
                                            </button>
                                            {' '}page.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={resetForm}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddSecret}
                                disabled={!title}
                                className="px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {editingSecret ? 'Update' : 'Add'} Secret
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Password Generator Modal */}
            {showPasswordGenerator && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-900">Generate Password</h2>
                            <button
                                onClick={() => {
                                    setShowPasswordGenerator(false);
                                    setGeneratedPassword('');
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* Generated Password Display */}
                            {generatedPassword && (
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between gap-4 mb-3">
                                        <code className="text-lg font-mono flex-1 break-all">{generatedPassword}</code>
                                        <button
                                            onClick={() => copyToClipboard(generatedPassword)}
                                            className="p-2 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0"
                                            title="Copy"
                                        >
                                            {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-gray-600" />}
                                        </button>
                                    </div>
                                    <button
                                        onClick={useGeneratedPassword}
                                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                                    >
                                        Use This Password
                                    </button>
                                </div>
                            )}

                            {/* Length */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Length: {genLength} characters
                                </label>
                                <input
                                    type="range"
                                    min="6"
                                    max="32"
                                    value={genLength}
                                    onChange={(e) => setGenLength(Number(e.target.value))}
                                    className="w-full"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>6</span>
                                    <span>32</span>
                                </div>
                            </div>

                            {/* Options */}
                            <div className="space-y-3">
                                {/* Lowercase (always on) */}
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <label className="text-sm font-medium text-gray-700">Lowercase (a-z)</label>
                                    <input
                                        type="checkbox"
                                        checked={true}
                                        disabled
                                        className="w-5 h-5"
                                    />
                                </div>

                                {/* Uppercase */}
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Uppercase (A-Z)</label>
                                        <input
                                            type="checkbox"
                                            checked={useUppercase}
                                            onChange={(e) => setUseUppercase(e.target.checked)}
                                            className="w-5 h-5"
                                        />
                                    </div>
                                    {useUppercase && (
                                        <div className="mt-2">
                                            <label className="block text-xs text-gray-600 mb-1">Count: {uppercaseCount}</label>
                                            <input
                                                type="range"
                                                min="1"
                                                max={Math.floor(genLength / 2)}
                                                value={uppercaseCount}
                                                onChange={(e) => setUppercaseCount(Number(e.target.value))}
                                                className="w-full"
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                <span>1</span>
                                                <span>{Math.floor(genLength / 2)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Numbers */}
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Numbers (0-9)</label>
                                        <input
                                            type="checkbox"
                                            checked={useNumbers}
                                            onChange={(e) => setUseNumbers(e.target.checked)}
                                            className="w-5 h-5"
                                        />
                                    </div>
                                    {useNumbers && (
                                        <div className="mt-2">
                                            <label className="block text-xs text-gray-600 mb-1">Count: {numbersCount}</label>
                                            <input
                                                type="range"
                                                min="1"
                                                max={Math.floor(genLength / 2)}
                                                value={numbersCount}
                                                onChange={(e) => setNumbersCount(Number(e.target.value))}
                                                className="w-full"
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                <span>1</span>
                                                <span>{Math.floor(genLength / 2)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Symbols */}
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-700">Symbols (!@#$...)</label>
                                        <input
                                            type="checkbox"
                                            checked={useSymbols}
                                            onChange={(e) => setUseSymbols(e.target.checked)}
                                            className="w-5 h-5"
                                        />
                                    </div>
                                    {useSymbols && (
                                        <div className="mt-2">
                                            <label className="block text-xs text-gray-600 mb-1">Count: {symbolsCount}</label>
                                            <input
                                                type="range"
                                                min="1"
                                                max={Math.floor(genLength / 2)}
                                                value={symbolsCount}
                                                onChange={(e) => setSymbolsCount(Number(e.target.value))}
                                                className="w-full"
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                <span>1</span>
                                                <span>{Math.floor(genLength / 2)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowPasswordGenerator(false);
                                    setGeneratedPassword('');
                                }}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Close
                            </button>
                            <button
                                onClick={generatePassword}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SecretsVault;

